/**
 * Workshop Selection Modal
 * Modal for selecting a workshop when user has access to multiple workshops
 */

import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Building2, MapPin, X } from "lucide-react";
import { Workshop } from "../types/workshop";

interface WorkshopSelectionModalProps {
  workshops: Workshop[];
  isOpen: boolean;
  onClose: () => void;
  onSelect: (workshop: Workshop) => void;
  canClose?: boolean;
}

const WorkshopSelectionModal: React.FC<WorkshopSelectionModalProps> = ({
  workshops,
  isOpen,
  onClose,
  onSelect,
  canClose = false,
}) => {
  const handleSelect = (workshop: Workshop) => {
    onSelect(workshop);
    if (canClose) {
      onClose();
    }
  };

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={canClose ? onClose : undefined}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="relative w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-phoenix-600 to-phoenix-700 px-6 py-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Building2 className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Select Workshop</h2>
                    <p className="text-phoenix-100 text-sm mt-1">
                      Choose a workshop to continue
                    </p>
                  </div>
                </div>
                {canClose && (
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="p-6">
              {workshops.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No Workshops Available
                  </h3>
                  <p className="text-gray-500">
                    You don't have access to any workshops yet.
                    <br />
                    Please contact your administrator.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {workshops.map((workshop) => (
                    <motion.button
                      key={workshop.id}
                      onClick={() => handleSelect(workshop)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="group relative p-6 bg-gradient-to-br from-gray-50 to-gray-100 
                               hover:from-phoenix-50 hover:to-phoenix-100 
                               border-2 border-gray-200 hover:border-phoenix-400 
                               rounded-xl transition-all duration-200 text-left"
                    >
                      {/* Workshop Icon */}
                      <div className="flex items-start gap-4">
                        <div
                          className="p-3 bg-white rounded-lg shadow-sm group-hover:bg-phoenix-500 
                                      group-hover:text-white transition-colors"
                        >
                          <Building2 className="w-6 h-6" />
                        </div>

                        <div className="flex-1 min-w-0">
                          {/* Workshop Name */}
                          <h3 className="text-lg font-bold text-gray-900 mb-1 group-hover:text-phoenix-700">
                            {workshop.name}
                          </h3>

                          {/* Location */}
                          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                            <MapPin className="w-4 h-4" />
                            <span className="truncate">
                              {workshop.location}
                            </span>
                          </div>

                          {/* Code Badge */}
                          <div
                            className="inline-flex items-center px-3 py-1 rounded-full 
                                        bg-white border border-gray-300 text-xs font-medium text-gray-700
                                        group-hover:bg-phoenix-100 group-hover:border-phoenix-300 group-hover:text-phoenix-700"
                          >
                            Code: {workshop.code}
                          </div>
                        </div>
                      </div>

                      {/* Hover Arrow */}
                      <div
                        className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 
                                    transform translate-x-2 group-hover:translate-x-0 transition-all"
                      >
                        <div
                          className="w-8 h-8 rounded-full bg-phoenix-500 text-white 
                                      flex items-center justify-center"
                        >
                          →
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {!canClose && workshops.length > 0 && (
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 text-center">
                  ⚠️ You must select a workshop to continue
                </p>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  // Render modal using portal to escape stacking context
  return createPortal(modalContent, document.body);
};

export default WorkshopSelectionModal;
